import {
  ImageTrack,
  LyricsTrack,
  MusicTrack,
  ShotTrack,
  TimelineTime,
  TimelineTrack,
  VideoTrack,
} from "../types";

export type PositionedItem<T extends TimelineTrack["items"][number]> = {
  item: T;
  start: TimelineTime;
  duration: TimelineTime;
};

export function getTrackDuration(track: TimelineTrack): TimelineTime {
  if (track.kind === "audio") {
    if (track.items.length === 0) return 0;
    const audio = track.items[0];
    return Math.max(0, audio.endTime - audio.startTime);
  }

  if (track.kind === "lyrics") {
    if (track.items.length === 0) return 0;
    return track.items.reduce<TimelineTime>((max, item) => {
      const duration = Math.max(0, item.endTime - item.startTime);
      const end = item.startTime + duration;
      return Math.max(max, end);
    }, 0);
  }

  return track.items.reduce<TimelineTime>((total, item) => {
    const itemDuration = "duration" in item ? item.duration : 0;
    return total + itemDuration;
  }, 0);
}

export function getTimelineDuration(tracks: TimelineTrack[]): TimelineTime {
  if (!tracks.length) return 0;
  return Math.max(...tracks.map((track) => getTrackDuration(track)));
}

export function buildTrackLayout(
  track: TimelineTrack
): PositionedItem<TimelineTrack["items"][number]>[] {
  if (track.items.length === 0) return [];

  if (
    track.kind === "lyrics" ||
    track.kind === "audio" ||
    track.kind === "music"
  ) {
    return track.items.map((item) => ({
      item,
      start: item.startTime,
      duration: Math.max(0, item.endTime - item.startTime),
    }));
  }

  return track.items.reduce<
    PositionedItem<
      | ImageTrack["items"][number]
      | VideoTrack["items"][number]
      | ShotTrack["items"][number]
      | MusicTrack["items"][number]
      | LyricsTrack["items"][number]
    >[]
  >((acc, item) => {
    const start =
      acc.length === 0
        ? 0
        : acc[acc.length - 1].start + acc[acc.length - 1].duration;
    return acc.concat({
      item,
      start,
      duration: "duration" in item ? item.duration : 0,
    });
  }, []);
}

export function computeSequentialLayout(
  track: TimelineTrack
): PositionedItem<TimelineTrack["items"][number]>[] {
  return buildTrackLayout(track);
}
